import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReveal } from '../../../hooks/useReveal';
import { supabase, Partner } from '../../../lib/supabase';

function PartnerCard({ p, lang }: { p: Partner; lang: string }) {
  const sector =
    (p.sector_i18n as Record<string, string> | undefined)?.[lang] ?? p.sector;

  return (
    <div className="flex-shrink-0 w-[168px] bg-white rounded-xl border border-silver-light p-5 flex flex-col items-center gap-2 hover:border-navy/30 transition-all duration-300 cursor-default">
      {p.logo_url && p.logo_url.trim() !== '' ? (
        <div className="w-12 h-12 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 bg-white border border-navy/10">
          <img
            src={p.logo_url}
            alt={p.name}
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div
          className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0"
          style={{ backgroundColor: p.color }}
        >
          <span className="text-white text-sm font-serif font-bold tracking-wide select-none">
            {p.initials}
          </span>
        </div>
      )}
      <p className="text-navy text-xs font-sans font-semibold text-center leading-tight">{p.name}</p>
      <span className="text-navy/40 text-[10px] font-sans text-center bg-navy/5 px-2 py-0.5 rounded-full whitespace-nowrap">
        {sector}
      </span>
    </div>
  );
}

export default function PartnersPreview() {
  const ref = useReveal();
  const { t, i18n } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('PartnersPreview fetch error:', error);
      } else if (data) {
        setPartners(data as Partner[]);
      }
      setLoading(false);
    };

    fetchPartners();
  }, []);

  const lang = i18n.language?.slice(0, 2) || 'ko';

  // 한 세트가 뷰포트보다 넓도록 최소 12개 이상 반복
  const MIN_COUNT = 12;
  const repeatCount = partners.length > 0
    ? Math.ceil(MIN_COUNT / partners.length)
    : 1;
  const loopItems = Array.from({ length: repeatCount }, () => partners).flat();

  return (
    <section ref={ref} className="bg-[#F8F9FB] py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="reveal text-center mb-14">
          <p className="text-xs font-sans font-semibold tracking-[0.2em] text-navy/40 uppercase mb-3">
            {t('partners.trustedBy')}
          </p>
          <h2 className="font-serif text-navy text-4xl font-bold">
            {t('partners.heading')}
          </h2>
          <p className="text-navy/50 text-base font-sans italic mt-2">{t('partners.sub')}</p>
        </div>
      </div>

      {/* Marquee Track */}
      {!loading && loopItems.length > 0 && (
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#F8F9FB] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#F8F9FB] to-transparent z-10 pointer-events-none" />
          <div
            className="marquee-track flex gap-4 px-4"
            style={{
              width: 'max-content',
              animationDuration: `${loopItems.length * 3}s`,
            }}
          >
            {loopItems.map((p, i) => (
              <PartnerCard key={`a-${p.id}-${i}`} p={p} lang={lang} />
            ))}
            {loopItems.map((p, i) => (
              <PartnerCard key={`b-${p.id}-${i}`} p={p} lang={lang} />
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6">
        <div className="reveal text-center mt-12">
          <Link
            to="/partners"
            className="inline-flex items-center gap-2 text-sm font-sans font-medium text-navy/60 hover:text-navy transition-colors duration-200 cursor-pointer border-b border-transparent hover:border-navy pb-0.5"
          >
            {t('partners.viewAll')} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
